import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds timeout
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log('API Response:', response.config.method?.toUpperCase(), response.config.url, response.status);
    }
    return response;
  },
  (error) => {
    // Log errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', error.response?.status, error.config?.url, error.response?.data);
    }

    // Handle network errors
    if (!error.response) {
      console.error('Network Error:', error.message);
      return Promise.reject(new Error('Network error. Please check your internet connection.'));
    }

    // 401 Unauthorized is handled by authService's interceptor (token refresh + logout),
    // which is registered separately so it can attempt a silent refresh before logging out.
    // Pass the original axios error through unchanged so `error.response` survives for it.
    if (error.response?.status === 401) {
      return Promise.reject(error);
    }

    // Below, every branch used to `reject(new Error(...))` with a generic,
    // hardcoded message and nothing else — which discards `error.response`.
    // Callers throughout the app (thunks, catch blocks) read the *specific*
    // backend reason via `error.response?.data?.message` (e.g. a 422 like
    // "The avatar must not be greater than 2048 kilobytes"); losing
    // `.response` meant that lookup always came back `undefined` and every
    // caller silently fell back to its own generic "Failed to ..." string —
    // so a photo upload rejected for being too large, wrong type, etc. always
    // surfaced the same unhelpful message with no way to tell why it failed.
    // Reattaching `.response` here keeps those specific messages intact while
    // still providing a sensible fallback `.message` for callers that don't
    // dig into `.response` themselves.
    const attachResponse = (err: Error) => {
      (err as any).response = error.response;
      return err;
    };

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      return Promise.reject(attachResponse(new Error(
        error.response?.data?.message || 'Access forbidden. You do not have permission to perform this action.'
      )));
    }

    // Handle 404 Not Found
    if (error.response?.status === 404) {
      return Promise.reject(attachResponse(new Error(
        error.response?.data?.message || 'Resource not found.'
      )));
    }

    // Handle 500 Server Error
    if (error.response?.status >= 500) {
      return Promise.reject(attachResponse(new Error(
        error.response?.data?.message || 'Server error. Please try again later.'
      )));
    }

    // Handle other HTTP errors (e.g. 422 validation failures)
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
    return Promise.reject(attachResponse(new Error(errorMessage)));
  }
);

export default api;